import classNames from "classnames";
import {useEffect, useMemo, useState} from "preact/hooks";
import {useTranslation} from "react-i18next";
import {useServices} from "@/services-context";
import {LayoutStyleTypes} from "@/util-react/layout";
import {BrandCard, BrandType, getList} from "@/elements-react/brands-list-images";
import FixBackButton from "@/elements-react/fix-back-button";
import {FixBreadcrumb, useBreadcrumbs} from "@/elements-react/fix-breadcrumb";
import {Url} from "@/helpers/url";
import {Icon} from "@/elements-react/fix-icon";
import searchIcon from "@/assets/search.svg";
import styles from "./brands-page.module.scss";
import {Input} from "@headlessui/react";

type BrandTabType = "spare" | "car";

type BrandListItem = ReturnType<typeof getList>[number] & {
  isCarBrand: boolean;
};

const getTabFromQuery = (query: string | undefined): BrandTabType => {
  const params = new URLSearchParams(query ?? "");
  const type = params.get("type");

  return type === "car" ? "car" : "spare";
};

export const BrandsPageCommon = ({ type }: { type: LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_LG }) => {
  const { t } = useTranslation();
  const services = useServices();

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<BrandTabType>(() =>
    getTabFromQuery(typeof window !== "undefined" ? window.location.search : ""),
  );

  useEffect(() => {
    setTab( getTabFromQuery(typeof window !== "undefined" ? window.location.search : ""))
  }, [window.location.search]);

  const list = useMemo<BrandListItem[]>(() => {
    const sorted = [...services.brands.list].sort((a, b) => a.name.localeCompare(b.name));
    const imageItems = getList(sorted);

    return imageItems.map((item, index) => ({
      ...item,
      isCarBrand: Boolean(sorted[index]?.isCarBrand),
    }));
  }, [services]);

  const isCarTab = tab === "car";
  const tabTitle = isCarTab
    ? t("n95cez3utj7rjo3x.car-manufacturers-title" as any, "Car manufacturers brands" as any)
    : t("iivb4hn8nv.manufactures-brands" as any, "Spare parts manufacturers brands" as any);

  const breadcrumbs = useMemo(() => {
    const rootUrl = Url.to("brands-page");

    return [
      {
        label: t("ffhx2rrz9oxfe95m.brands" as any, "Brands" as any),
        url: rootUrl,
      },
      {
        label: tabTitle,
        url: `${rootUrl}?type=${tab}`,
      },
    ];
  }, [tab, tabTitle]);

  useBreadcrumbs(breadcrumbs);

  const visibleBrands = useMemo(() => {
    const query = search.trim().toLowerCase();

    return list.filter((brand) => {
      if (brand.isCarBrand !== isCarTab) {
        return false;
      }

      if (!query.length) {
        return true;
      }

      return brand.name.toLowerCase().includes(query);
    });
  }, [list, isCarTab, search]);

  return (
    <div className={classNames(styles.page, styles[type])}>
      <div className={styles.container}>
        <div className={styles.navigation}>
          {type === LayoutStyleTypes.TYPE_LG && <FixBackButton url={Url.to('home-page-with-lang')} />}
          {type === LayoutStyleTypes.TYPE_LG && <FixBreadcrumb />}
        </div>

        <div className={styles.tabs}>
          <a
            className={classNames(styles.tab, {
              [styles.activeTab]: !isCarTab,
            })}
            href={Url.to("brands-page", {}, `?type=spare`)}
          >
            {t("uc1p5774isud3lg7.spare-parts-manufacturers" as any, "Spare parts manufacturers" as any)}
          </a>

          <a
            className={classNames(styles.tab, {
              [styles.activeTab]: isCarTab,
            })}
            href={Url.to("brands-page", {}, `?type=car`)}
          >
            {t("3qbniuvbxv.car-brands" as any, "Car manufacturers" as any)}
          </a>
        </div>

        <h1 className={styles.title}>{tabTitle}</h1>
        <p className={styles.subtitle}>
          {t("0qwhx4m5rw5g5fau.brands-subtitle" as any, "Browse car manufacturers and spare parts brands available in stock" as any)}
        </p>

        <div className={styles.searchWrap}>
          <Input
            value={search}
            onInput={(event) => setSearch((event.target as HTMLInputElement).value)}
            className={styles.searchInput}
            placeholder={t("6a5vbfoieqwa6n5t.find-a-brand" as any, "Find a brand" as any)}
          />
          <Icon src={searchIcon as string} width={18} height={18} className={styles.searchIcon} />
        </div>

        <div className={styles.grid}>
          {visibleBrands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand as BrandType}
              className={styles.card}
              showNameUnderLogo={true}
              nameClassName={styles.cardName}
            />
          ))}
        </div>

        {!visibleBrands.length && <p className={styles.empty}>{t("jzlk0w1qi95v5c7e.no-brands" as any, "No brands found" as any)}</p>}
      </div>
    </div>
  );
};
